import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@furality/frontend-stack';
import GuestView from '@/components/_private-parties/GuestView.tsx';
import HostView from '@/components/_private-parties/HostView.tsx';
import { FullLayout } from '@/components/layouts/index.tsx';
import InlineLoader from '@/components/loading/InlineLoader.tsx';
import hash from '@/lib/helpers/hash.ts';
import useFlag from '@/lib/hooks/useFlag.ts';

const PrivatePartiesViewInternal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const enableSquads = useFlag('squads');

  const currentHash = useMemo(() => hash(location) || 'host', [location]);

  const { t } = useTranslation(['privateParties']);

  useEffect(() => {
    if (!enableSquads) {
      void navigate({ to: '/', replace: true });
    }
  }, [enableSquads, navigate]);

  const changeTab = (newTab: string | null) => {
    if (newTab === null) {
      return;
    }

    void navigate({ hash: newTab, replace: true, resetScroll: false });
  };

  const auth = useAuth();
  if (auth.state !== 'loggedIn') {
    return <InlineLoader />;
  }

  return (
    <div className="mt-4 mb-md-4">
      <Tabs activeKey={currentHash} onSelect={(newTab) => changeTab(newTab)}>
        <Tab eventKey="host" title={t('privateParties:host.tab-name')}>
          <HostView />
        </Tab>
        <Tab eventKey="join" title={t('privateParties:guest.tab-name')}>
          <GuestView />
        </Tab>
      </Tabs>
    </div>
  );
};

export const Route = createFileRoute('/private-parties')({
  component: () => (
    <FullLayout title="shared.navigation.privateParties">
      <PrivatePartiesViewInternal />
    </FullLayout>
  ),
});
